// TestSelector
// -------------
// Dropdown listing the webapp's tests by date, newest first. Drives
// the WebApplication page's "which test am I inspecting" state — the
// Findings tiles, vuln table, and Report generation all read from
// whichever test is selected here.
//
// In-flight tests (Provisioning / Running / Stopping) are included
// and labelled "Live" so the user sees the currently-running test
// appear in the dropdown the moment they click Pentest Now. Picking
// it shows the live placeholder state (zeroed tiles + "Test in
// progress" vuln table). When the test completes its label flips to
// the terminal status and the surfaces fill in with real data.
//
// Wraps SortSelect so the dropdown is pixel-identical to every other
// dropdown on the page (vuln table sort, Applications sort, etc.) —
// no parallel styling to keep in sync.

import React from "react";
import { Calendar } from "lucide-react";

import SortSelect from "@/components/common/SortSelect";
import TestResultsClass from "@/models/testResult";
import {
  TERMINAL_TEST_STATUSES,
  TEST_STATUS_PROVISIONING,
  TEST_STATUS_RUNNING,
  TEST_STATUS_STOPPING,
} from "@/constants/definitions";
import { getTestResultLabel } from "@/utils/testStatusLabel";
import { formatDayMonth } from "@/utils/date";

const IN_FLIGHT_STATUSES: ReadonlySet<string> = new Set([
  TEST_STATUS_PROVISIONING,
  TEST_STATUS_RUNNING,
  TEST_STATUS_STOPPING,
]);

interface TestSelectorProps {
  tests: TestResultsClass[];
  selectedTestId: string;
  onChange: (testId: string) => void;
}

export default function TestSelector({
  tests,
  selectedTestId,
  onChange,
}: TestSelectorProps) {
  // Terminal + in-flight tests, newest first. The list shape is
  // computed here so consumers don't repeat the filter / sort.
  // In-flight entries are labelled "Live" — same row in the dropdown,
  // distinctive label so the user can tell what's a completed
  // assessment vs the run currently underway.
  const options = tests
    .filter((test) => {
      if (!test.testStatus || !test.id) return false;
      return (
        TERMINAL_TEST_STATUSES.has(test.testStatus) ||
        IN_FLIGHT_STATUSES.has(test.testStatus)
      );
    })
    .sort((a, b) => Number(b.startTime ?? 0) - Number(a.startTime ?? 0))
    .map((test) => ({
      value: test.id,
      label: `${formatDayMonth(Number(test.startTime))} · ${
        getTestResultLabel({
          testStatus: test.testStatus,
          endTrigger: test.endTrigger,
        }).shortLabel
      }`,
    }));

  if (options.length === 0) {
    // No terminal tests yet (brand-new app, or an app whose only
    // attempt is still in-flight). Render nothing — the page's other
    // surfaces (Pentest button, empty Findings tiles) already convey
    // "no completed assessments yet".
    return null;
  }

  return (
    <SortSelect<string>
      id="test-selector"
      value={selectedTestId}
      onChange={onChange}
      options={options}
      ariaLabel="Select a previous test"
      icon={Calendar}
    />
  );
}
